import { Suspense, lazy } from 'react';
import { useT } from '../lib/idioma.js';
import { variaveisDoTema } from '../lib/tema.js';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';

/**
 * Minhas aulas — a tela que os dois botões da sala prometiam e não tinham.
 *
 * ## O defeito que ela fecha
 *
 * `♥ Favoritar` e `⏱ Assistir depois` gravavam num endpoint completo
 * (`GET /a/:areaSlug/minhas-aulas`, com filtro de visibilidade e tudo), e
 * **nenhuma rota do app do aluno o lia**. O aluno marcava e não tinha para onde
 * ir ver. A régua da auditoria diz a frase inteira: *"favorito que não tem lista
 * é botão decorativo"* — e um botão que promete uma lista inexistente é pior que
 * a ausência do recurso, porque ensina que os controles daqui não valem nada.
 *
 * ## Por que uma tela com DUAS listas, e não duas telas nem uma lista só
 *
 * Uma lista só juntaria coisas que respondem a perguntas diferentes, e a mesma
 * aula marcada das duas formas apareceria **duas vezes na mesma lista** — que é
 * exatamente o que não pode acontecer. Duas telas cobrariam duas portas no
 * cabeçalho para dois punhados de aulas.
 *
 * A ordem também é uma afirmação: a fila vem **primeiro**, porque ela é a que
 * tem uma pergunta pendente ("o que eu ia ver?"). Favoritos é acervo.
 *
 * O desenho das duas mora em `componentes/FilaEAcervo.tsx`, e o argumento de por
 * que elas **não podem ter a mesma forma** está no cabeçalho de lá.
 *
 * ## Por que este arquivo é só uma casca
 *
 * Por causa de V13, na redação de 08/08 (§19.4): o que se orça é o **pedaço de
 * entrada**, o que todo aluno baixa antes de ver qualquer coisa. Medido no
 * commit desta entrega, antes dela, o pedaço de entrada estava em **355,5 kB**
 * contra a base de 320,6 — **34,9 dos 40 kB** do teto, com 5,1 de folga. Um
 * catálogo com capas, grade e estados vazios desenhados não cabe em 5 kB.
 *
 * Então esta tela passa a ser sob demanda, pela mesma técnica e pelo mesmo
 * argumento que `SalaDeAula` já usa em `App.tsx`: ela **não é a primeira**. A
 * porta da área é a vitrine, e quem abre a vitrine não deve pagar por uma tela
 * que talvez não abra nesta visita. E a divisão é real, no sentido da terceira
 * cláusula de V13: o pedaço só é alcançável por esta rota — nenhuma outra tela
 * o importa, nem estática nem dinamicamente.
 *
 * O corte é aqui, e não em `App.tsx`, porque `App.tsx` registra a rota e não
 * precisa saber como ela se carrega; e porque um `lazy()` a mais lá em cima
 * seria uma linha a mais no arquivo que todas as sessões editam ao mesmo tempo.
 */
const Miolo = telaComDicionario(() =>
  import('../componentes/FilaEAcervo.js').then((m) => ({ default: m.FilaEAcervo })),
);

export function PaginaDeMinhasAulas() {
  const t = useT();
  return (
    <Suspense
      fallback={
        /* A MESMA frase que a tela mostraria enquanto busca os dados, e sobre o
           mesmo fundo: sem `variaveisDoTema` o `--fundo` não existe ainda e o
           quadro entre o clique e o pedaço piscaria branco. O tema da área só
           chega com os dados; o padrão aqui é o escuro de fábrica, que é o mesmo
           do `index.css`. */
        <div className="min-h-screen" style={variaveisDoTema()}>
          <p className="px-6 pt-10 text-sm" style={{ color: 'var(--texto-suave)' }}>
            {t('estado.carregando')}
          </p>
        </div>
      }
    >
      <Miolo />
    </Suspense>
  );
}
